

/**
 * Metric Cell Component
 * Renders cell value based on metric type
 */

import { Star, Check, X } from 'lucide-react';
import { MetricCellProps } from '@/types/comparison';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

export function MetricCell({
  value,
  metric,
  isBest = false,
  isHighlighted = false,
}: MetricCellProps) {
  if (value === undefined || value === null) {
    return (
      <span className="text-muted-foreground text-sm">N/A</span>
    );
  }

  switch (metric.type) {
    case 'boolean':
      return (
        <div className="flex items-center justify-center">
          {value ? (
            <div className={cn(
              'flex items-center gap-1 text-green-600',
              isBest && 'font-semibold'
            )}>
              <Check className="h-5 w-5" />
              <span className="text-sm">Yes</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-muted-foreground">
              <X className="h-5 w-5" />
              <span className="text-sm">No</span>
            </div>
          )}
        </div>
      );

    case 'rating':
      return (
        <div className={cn(
          'flex items-center justify-center gap-1',
          isBest && 'text-amber-600 font-semibold'
        )}>
          <Star className="h-5 w-5 fill-current" />
          <span className="text-lg">{Number(value).toFixed(1)}</span>
        </div>
      );

    case 'number':
      return (
        <div className={cn(
          'text-center',
          isBest && 'font-semibold text-primary'
        )}>
          <span className="text-lg">{Number(value).toLocaleString()}</span>
        </div>
      );

    case 'array':
      if (Array.isArray(value)) {
        // If it's an array of objects (like certifications)
        if (value.length > 0 && typeof value[0] === 'object') {
          const labels = value.map((item: { name?: string }) => item.name || JSON.stringify(item));
          return (
            <div className="flex flex-wrap justify-center gap-1">
              {labels.slice(0, 3).map((label: string, index: number) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {label}
                </Badge>
              ))}
              {labels.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{labels.length - 3}
                </Badge>
              )}
            </div>
          );
        }
        
        // Simple string array
        if (value.length === 0) {
          return <span className="text-muted-foreground text-sm">None</span>;
        }
        
        return (
          <div className={cn(
            'text-center',
            isBest && 'font-semibold text-primary'
          )}>
            <span className="text-lg">{value.length}</span>
            <span className="text-xs text-muted-foreground ml-1">
              {value.length === 1 ? 'item' : 'items'}
            </span>
          </div>
        );
      }
      return <span className="text-sm">{String(value)}</span>;

    case 'text':
    default:
      return (
        <div className={cn(
          'text-center',
          isBest && 'font-semibold text-primary'
        )}>
          <span className="text-sm">
            {metric.format ? metric.format(value) : String(value)}
          </span>
        </div>
      );
  }
}
